import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { UpdateEntityStore } from "../../common/store/types";
import { phonogramService } from "../services/phonogramService";
import {
  PhonogramValuesInit,
  getPhonogramValues,
  PhonogramValuesType,
  PhonogramErrorsType,
  phonogramDisplayId,
} from "./PhonogramCommonStore";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { Phonogram, SortOrder } from "../../../api/graphql";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getInterpreterValues,
  InterpreterValuesType,
  interpreterDisplayId,
} from "../../interpreter/store/InterpreterCommonStore";
import { actualStatusList } from "../../common/types";
import { addedStatusList } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { isNil } from "../../../common/utils";
import { MusicalWorkLinkUpdateStore } from "../../musicalWorkLink/store";
import { uploadFailureReason } from "../../../common/utils";
import { apiUploadFile } from "../../../api/apiClient";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { GridChangeSet } from "../../common/types";
import { restoredStatus } from "../../common/types";
import { isOriginalCrudStatus } from "../../common/types";
import { RowCrudStatus } from "../../common/types";
import { updatedStatus, deleteStatus } from "../../common/types";
import { interpreterService } from "../../interpreter/services/interpreterService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class PhonogramUpdateStore implements UpdateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  confirmDialogOpened: boolean = false;
  confirmDialogTitle: string = "";
  confirmDialogDescription: string = "";
  confirmDialogCallback: (() => void) | null = null;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: PhonogramErrorsType = {};
  service = phonogramService;
  phonogramInstance: Phonogram | null = null;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
    phonogramInfo: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda (envío y relectura): la capa del formulario (SavingOverlay)
  // lo bloquea, así nada de lo que se escribiera lo pisa la relectura.
  isSaving: boolean = false;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = PhonogramValuesInit();
  trackNameError: string | undefined;
  performersError: string | undefined;
  performersService = interpreterService;
  performersSearchValue: string = "";
  performersOptions: InterpreterValuesType[] = [];
  albumNameError: string | undefined;
  publicationYearError: string | undefined;
  isrcError: string | undefined;
  originalProducerError: string | undefined;
  audioFileError: string | undefined;
  mainAudioError: string | undefined;
  linksError: string | undefined;
  linksVisibleColumns: { name: string; title: string }[];
  linksColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
      multi?: boolean;
      onChange?: (value: string) => void;
      onSelect?: (value: any) => void;
      getOptions?: () => { displayId: string; id: string }[];
      choices?: {
        min: number;
        max: number;
        unitSingular?: string;
        unitPlural?: string;
        nullText?: string;
      };
    }
  >;
  linksUpdateStore: MusicalWorkLinkUpdateStore;
  notesError: string | undefined;
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";
  private uniqueCounter = 1;
  dummyCRUDRestoredObservable: number = 0;
  private customValidators: CustomValidator<PhonogramValuesType>[] = [];
  private _ownPendingChanges = false;
  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction?: () => void;
  originalState: any = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Drawer mode flag - when true, close() pops/closes the drawer instead of navigating
  isDrawerMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: { resultId?: string; cancelled?: boolean }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();
    this.linksVisibleColumns = [
      {
        name: "__platform",
        title: this.langStore.MusicalWorkLinkPlatformLabel,
      },
      { name: "__type", title: this.langStore.MusicalWorkLinkTypeLabel },
      { name: "url", title: this.langStore.MusicalWorkLinkUrlLabel },
    ];
    this.linksColumnTypes = {
      __platform: {
        fieldType: "__Reference" as const,
        nullable: true,
        readOnly: false,
        multi: false,
        onChange: (value: string) =>
          this.linksUpdateStore.setPlatformValue(value),
        onSelect: (value: any) =>
          this.linksUpdateStore.setSelectedPlatform(value),
        getOptions: () => this.linksUpdateStore.getPlatformOptions(),
      },
      __type: {
        fieldType: "__Reference" as const,
        nullable: true,
        readOnly: false,
        multi: false,
        onChange: (value: string) => this.linksUpdateStore.setTypeValue(value),
        onSelect: (value: any) => this.linksUpdateStore.setSelectedType(value),
        getOptions: () => this.linksUpdateStore.getTypeOptions(),
      },
      url: { fieldType: "String" as const, nullable: false, readOnly: false },
    };
    this.linksUpdateStore = new MusicalWorkLinkUpdateStore(
      rootStore,
      "links_" + this.storeId
    );
    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(v as CustomValidator<PhonogramValuesType>);
      }
    });
    makeAutoObservable(this, {
      originalState: false,
    });
    this.setupChangeDetection();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }

    if (this.linksUpdateStore) {
      this.linksUpdateStore.destroy();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<PhonogramValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  *loadPhonogramById(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    this.phonogramInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (this.phonogramInstance) {
      this.entityValues = getPhonogramValues(
        this.phonogramInstance,
        this.langStore
      );
      this.saveOriginalState();
    }
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  // Guardar deshabilitado: mientras se guarda.
  get saveDisabled() {
    return this.isProcessing;
  }

  get displayId(): string {
    if (this._ownPendingChanges && this.entityValues) {
      return phonogramDisplayId(this.entityValues as any, this.langStore);
    }
    if (this.phonogramInstance) {
      return phonogramDisplayId(this.phonogramInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.PhonogramSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setCRUDRowsFromValues(changes: GridChangeSet, values: { string: any }) {
    if (changes.added) {
      changes.added = [values];
    } else if (changes.changed) {
      changes.changed = [values];
    }
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "phonogramInfo",
        label: this.rootStore.langStore.groupPhonogramPhonogramInfoLabel,
        errors: this.getCardErrors("phonogramInfo"),
      },
    ];
  }

  *returnedData(data: any): any {
    const { callerData, returnData } = data;
    if (
      callerData &&
      returnData &&
      !returnData.cancelled &&
      (returnData.entity || returnData.resultId)
    ) {
      // Delegate to subentity UpdateStore if callerData.entityName matches a subentity
      if (callerData?.entityName === "MusicalWorkLink") {
        this.linksUpdateStore.isWaitingForCallback = false;
        this.linksUpdateStore.callbackReturnPath = null;
        yield this.linksUpdateStore.returnedData(data);
        return;
      }

      if (callerData?.fieldName === "performers") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            interpreterDisplayId(returnData.entity, this.langStore);
          this.entityValues.showPerformers.push({
            id: returnData.entity.id,
            displayId,
          });
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.interpreterOne({ id: returnData.resultId });
          const interpreter = result.interpreterOne;
          this.entityValues.showPerformers.push({
            displayId: interpreter.displayId,
            id: interpreter.id,
          });
          this.setPerformersOptions([
            getInterpreterValues(interpreter, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  showConfirmDialog(title: string, description: string, callback: () => void) {
    this.confirmDialogTitle = title;
    this.confirmDialogDescription = description;
    this.confirmDialogCallback = callback;
    this.confirmDialogOpened = true;
  }

  closeConfirmDialog() {
    this.confirmDialogOpened = false;
    this.confirmDialogCallback = null;
  }

  *executeConfirmedAction() {
    const callback = this.confirmDialogCallback;
    this.closeConfirmDialog();
    if (callback) {
      yield callback();
    }
  }

  getTrackNameValue(): string {
    return this.entityValues.trackName;
  }

  setTrackNameValue(value: string) {
    this.entityValues.trackName = value;
  }

  setPerformersOptions(values: InterpreterValuesType[]) {
    this.performersOptions = values;
  }

  *loadPerformersValues(): any {
    let where: any = null;
    if (this.performersSearchValue) {
      const searchFilter = {
        name: { contains: this.performersSearchValue, mode: "insensitive" },
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.interpreterListWithOffset({
      offset: 0,
      take: 100,
      where: where,
      orderBy: [{ lastUpdatedAt_: SortOrder.Desc }],
    });
    this.setPerformersOptions(
      (result.interpreterListWithOffset || []).map((x: any) =>
        getInterpreterValues(x, this.langStore)
      )
    );
  }

  getPerformersOptions() {
    return this.performersOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setPerformersValue(value: string) {
    this.performersSearchValue = value;
    yield this.loadPerformersValues();
  }

  setSelectedPerformers(values: { displayId: string; id: string }[]) {
    if (values.length > 0 && values[values.length - 1].id === "add-new") {
      const notFound = /".*?"/.exec(values[values.length - 1].displayId);
      this.addNewInterpreter(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "performers"
      );
    } else {
      this.entityValues.showPerformers = values;
      this.entityValues.__performers = values.map((v) => v.displayId);
    }
  }

  getAlbumNameValue(): string {
    return this.entityValues.albumName;
  }

  setAlbumNameValue(value: string) {
    this.entityValues.albumName = value;
  }

  getPublicationYearValue(): number | undefined {
    return this.entityValues.publicationYear;
  }

  setPublicationYearValue(value: number | undefined) {
    this.entityValues.publicationYear = value;
  }

  getIsrcValue(): string {
    return this.entityValues.isrc;
  }

  setIsrcValue(value: string) {
    this.entityValues.isrc = value;
  }

  getOriginalProducerValue(): string {
    return this.entityValues.originalProducer;
  }

  setOriginalProducerValue(value: string) {
    this.entityValues.originalProducer = value;
  }

  audioFileRestoreFile(id: string) {
    const file = this.entityValues.__audioFile_files.find((x) => x.id === id);
    if (!file) return;

    const newStatus = restoredStatus(file.__status__);

    const activeFiles = this.entityValues.__audioFile_files.filter((x) =>
      actualStatusList.includes(x.__status__)
    );
    if (activeFiles.length >= 1 && newStatus !== file.__status__) {
      alert("No puede haber más de un archivo activo");
      return;
    }

    file.__status__ = newStatus;
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  *audioFileUploadFile(file: File): any {
    const previousFiles = [...this.entityValues.__audioFile_files];
    const previousName = this.entityValues.audioFile;
    const fileId = `${this.uniqueCounter++}`;
    const newFile = {
      __status__: "added" as const,
      id: fileId,
      name: file.name,
      size: file.size,
      formattedSize: formatBytes(file.size),
      mimetype: file.type,
      ticket: "",
    };
    this.entityValues.__audioFile_files.push(newFile);
    this.entityValues.audioFile = file.name;
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;

    const outcome = yield this.uploadTicket(file);
    if (!outcome.ticket) {
      // La subida no terminó: el servidor no tiene el archivo, así que no
      // puede quedar en la lista. Vuelve lo que había y el campo dice por qué.
      this.entityValues.__audioFile_files = previousFiles;
      this.entityValues.audioFile = previousName;
      this.dummyCRUDRestoredObservable = this.uniqueCounter++;
      if (outcome.cancelled) {
        // Lo canceló el usuario desde el modal de espera: no es una falla.
        this.clearFieldError("audioFile");
      } else {
        this.setFieldError(
          "audioFile",
          this.langStore.PhonogramAudioFileLabel,
          this.langStore.uploadFailedMessage(file.name, outcome.reason)
        );
      }
      return;
    }
    this.clearFieldError("audioFile");
    const fileIndex = this.entityValues.__audioFile_files.findIndex(
      (f) => f.id === fileId
    );
    if (fileIndex !== -1) {
      this.entityValues.__audioFile_files[fileIndex].ticket = outcome.ticket;
    }
  }

  audioFileCommitFile(changes: GridChangeSet) {
    if (changes.deleted) {
      const id = changes.deleted[0];
      let index = 0;
      let toRemove;
      for (let file of this.entityValues.__audioFile_files) {
        if (file.id === id) {
          if (isOriginalCrudStatus(file.__status__)) {
            file.__status__ = "original-deleted";
          } else {
            toRemove = index;
          }
          break;
        }
        index++;
      }
      if (toRemove) {
        this.entityValues.__audioFile_files.splice(toRemove, 1);
      }
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  getMainAudioValue(): boolean {
    return this.entityValues.mainAudio;
  }

  setMainAudioValue(value: boolean) {
    this.entityValues.mainAudio = value;
  }

  private get linkshasChanges() {
    return (
      this.entityValues.showLinks?.some(
        (item: any) =>
          item.__status__ !== "original" && item.__status__ !== "empty"
      ) ?? false
    );
  }

  linksLoadRow(row: any) {
    this.linksUpdateStore.loadValuesFromRow(row);
  }

  linksRestore(id: string) {
    this.entityValues.showLinks = this.entityValues.showLinks.map((item) =>
      item.id === id
        ? { ...item, __status__: restoredStatus(item.__status__) }
        : item
    );
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  linksValidateChanges(_changes: GridChangeSet) {
    const errors = this.linksUpdateStore.validateData(
      this.linksUpdateStore.entityValues
    );

    this.linksUpdateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  linksCommitInlineChange(rowId: string, changes: Record<string, any>) {
    this.entityValues.showLinks = this.entityValues.showLinks.map((item) =>
      item.id === rowId
        ? { ...item, ...changes, __status__: updatedStatus(item.__status__) }
        : item
    );
  }

  linksCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.linksUpdateStore.entityValues;
      const hadChanges = this.linksUpdateStore.pendingChanges;
      if (hadChanges) {
        const links = this.entityValues.showLinks;
        for (
          let index = 0;
          index < this.entityValues.showLinks.length;
          index++
        ) {
          if (links[index].id === values.id) {
            this.entityValues.showLinks = this.entityValues.showLinks.map(
              (item, idx) =>
                idx === index
                  ? { ...values, __status__: updatedStatus(values.__status__) }
                  : item
            );
            break;
          }
        }
        this.linksUpdateStore.clearFields();
      }
    } else if (changes.added) {
      const values = this.linksUpdateStore.entityValues;
      const newId = `${this.uniqueCounter++}`;
      if (!this.entityValues.showLinks) {
        this.entityValues.showLinks = [];
      }
      this.entityValues.showLinks = [
        ...this.entityValues.showLinks,
        {
          ...values,
          id: newId,
          __status__: "added",
        },
      ];
      this.linksUpdateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const links = this.entityValues.showLinks;
      for (let index = 0; index < this.entityValues.showLinks.length; index++) {
        if (links[index].id === id) {
          if (isOriginalCrudStatus(links[index].__status__)) {
            this.entityValues.showLinks = this.entityValues.showLinks.map(
              (item, idx) =>
                idx === index
                  ? { ...item, __status__: deleteStatus(item.__status__) }
                  : item
            );
          } else {
            this.entityValues.showLinks = this.entityValues.showLinks.filter(
              (x) => x.id !== id
            );
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.links);
    //   this.setCRUDRowsFromValues(newChanges, this.linksUpdateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.links = getNewCRUDRows(newChanges, this.entityValues.links, this.uniqueCounter++);
    // }
  }

  getNotesValue(): string {
    return this.entityValues.notes;
  }

  setNotesValue(value: string) {
    this.entityValues.notes = value;
  }

  *fileUpload(file: File): any {
    const data = yield apiUploadFile(true, file, this.rootStore.activityStore);
    // Una subida cancelada vuelve sin datos.
    return data?.singleUpload?.ticket;
  }

  *uploadTicket(file: File): any {
    // No tira: una subida que falla es un resultado que el campo tiene que
    // mostrar, no una excepción que se pierde en la consola.
    try {
      const ticket = yield this.fileUpload(file);
      // Sin ticket y sin error es un pedido abortado: el botón Cancelar.
      return ticket ? { ticket } : { cancelled: true };
    } catch (error) {
      return {
        reason: uploadFailureReason(error, this.langStore.uploadConnectionLost),
      };
    }
  }

  setFieldError(field: string, fieldLabel: string, message: string) {
    this.errors = {
      ...this.errors,
      [field]: { message, fieldLabel },
    } as PhonogramErrorsType;
  }

  clearFieldError(field: string) {
    if (!(field in this.errors)) return;
    const errors: Record<string, unknown> = { ...this.errors };
    delete errors[field];
    this.errors = errors as PhonogramErrorsType;
  }

  *fileDownload(props: any, fieldName: string): any {
    // En el modal anidado (subentidad editada desde el parent) el instance
    // no se carga (loadValuesFromRow): el id sale de los values de la fila.
    // Sin el fallback, la URL iba con instanceId=undefined → 404 silencioso.
    const instanceId = this.phonogramInstance?.id ?? this.entityValues.id;
    yield downloadWithAuth(
      {
        entityName: "Phonogram",
        fieldName: fieldName,
        instanceId,
        fileName: props.name,
      },
      undefined,
      this.rootStore.activityStore
    );
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      // Mismo fallback que fileDownload: en el modal anidado el instance
      // no se carga y el id viene de los values de la fila.
      const instanceId = this.phonogramInstance?.id ?? this.entityValues.id;
      const blob = yield fetchFileBlob({
        entityName: "Phonogram",
        fieldName: fieldName,
        instanceId,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  getRowFromInstance(instance: Phonogram) {
    const row: any = {};
    row.version_ = instance.version_;
    row.id = instance.id;
    row.trackName = instance.trackName;
    row.albumName = instance.albumName;
    row.publicationYear = instance.publicationYear;
    row.isrc = instance.isrc;
    row.originalProducer = instance.originalProducer;
    row.mainAudio = instance.mainAudio;
    row.notes = instance.notes;
    return row;
  }

  addNewInterpreter(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "Phonogram",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/interpreter/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.phonogramInstance = null;
    this.entityValues = PhonogramValuesInit();
    this.cardState = {
      null: { open: true, errors: [] },
      phonogramInfo: { open: true, errors: [] },
    };
    this.performersSearchValue = "";
    this.performersOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: PhonogramValuesType): PhonogramErrorsType {
    const errors: PhonogramErrorsType = {};
    this.clearErrors();
    values.trackName = values.trackName.trim();
    if (values.trackName === "") {
      errors.trackName = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PhonogramTrackNameLabel,
      };
      this.setCardError("phonogramInfo", errors.trackName.message);
    }

    if (
      values.__audioFile_files.filter((f: any) =>
        actualStatusList.includes(f.__status__)
      ).length === 0
    ) {
      errors.audioFile = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PhonogramAudioFileLabel,
      };
      this.setCardError("phonogramInfo", errors.audioFile.message);
    }
    {
      const pendingUpload = values.__audioFile_files.find(
        (f: any) => addedStatusList.includes(f.__status__) && !f.ticket
      );
      if (pendingUpload) {
        errors.audioFile = {
          message: this.langStore.uploadNotFinishedMessage(pendingUpload.name),
          fieldLabel: this.langStore.PhonogramAudioFileLabel,
        };
        this.setCardError("phonogramInfo", errors.audioFile.message);
      }
    }

    if (isNil(values.mainAudio)) {
      errors.mainAudio = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PhonogramMainAudioLabel,
      };
      this.setCardError("phonogramInfo", errors.mainAudio.message);
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined,
      "PhonogramUpdateStore"
    );
  }

  close(returnData?: { resultId?: string; cancelled?: boolean }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.isDrawerMode) {
      // In drawer mode, close or pop the drawer instead of navigating
      if (this.rootStore.drawerViewStack.length > 1) {
        this.rootStore.popDrawerView();
      } else {
        this.rootStore.closeDrawerView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else {
      this.rootStore.router.goBack();
    }
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges || this.hasArrayChanges();
  }

  private hasArrayChanges(): boolean {
    return this.linkshasChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      trackName: this.entityValues.trackName,
      albumName: this.entityValues.albumName,
      publicationYear: this.entityValues.publicationYear,
      isrc: this.entityValues.isrc,
      originalProducer: this.entityValues.originalProducer,
      audioFile: this.entityValues.audioFile,
      mainAudio: this.entityValues.mainAudio,
      notes: this.entityValues.notes,
      performers: (this.entityValues.showPerformers || [])
        .map((x: any) => x.id)
        .sort(),
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  resetPendingChanges() {
    this._ownPendingChanges = false;
  }

  private normalizeSubentitiesAfterSave() {
    this.entityValues.showLinks = this.entityValues.showLinks
      .filter(
        (item: any) =>
          item.__status__ !== "original-deleted" &&
          item.__status__ !== "added-deleted"
      )
      .map((item: any) => ({
        ...item,
        __status__: "original" as RowCrudStatus,
      }));
  }

  getChangedFields(): string[] {
    if (!this.pendingChanges) return [];

    const changed: string[] = [];

    if (this._ownPendingChanges && this.originalSnapshot) {
      const current = this.computeSnapshot();

      Object.keys(current).forEach((key) => {
        if (!_.isEqual(current[key], this.originalSnapshot![key])) {
          changed.push(key);
        }
      });
    }
    if (this.linkshasChanges) changed.push("links");
    return changed;
  }

  *savePhonogram(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: el primero
    // cambia la versión del registro, y el segundo fallaría con RECORD_MODIFIED
    // aunque los datos ya estén guardados.
    if (this.saveDisabled) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que haga correr la relectura y el
      // cierre como si éste hubiera salido bien.
      this.savedId = null;
      // Desde acá hasta que termina (también la relectura), el formulario está
      // bloqueado por su capa. Pase lo que pase (también si el manejo del error
      // tira), el finally de afuera lo libera.
      this.isSaving = true;
      try {
        try {
          this.status = "processing";
          yield this._save();
        } catch (error) {
          this.status = "error";

          if (error instanceof Error) {
            const apiError = getApiError(error, this.langStore);
            this.showErrorDialog(
              apiError.code,
              apiError.title,
              apiError.message
            );
          } else {
            this.showErrorDialog(this.langStore.serverError, undefined);
          }
        }

        if (!this.savedId) {
          // Sin guardado exitoso (falló, o no había nada que guardar): Guardar se
          // vuelve a habilitar.
          if (this.isProcessing) this.status = "ok";
        } else {
          try {
            this.normalizeSubentitiesAfterSave();
            this.resetPendingChanges();
            this.saveOriginalState();
          } finally {
            // Hasta acá sigue guardando (isProcessing): Guardar deshabilitado, y un
            // segundo guardado no corre mientras se relee.
            this.status = "ok";
          }
          this.close({ resultId: this.savedId, cancelled: false });
        }
      } finally {
        // Pase lo que pase (también si el manejo del error tira), el formulario
        // se libera: la capa se cierra.
        this.isSaving = false;
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  *cancelPhonogram(): any {
    if (this.pendingChanges) {
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.exitWithoutSaving,
        this.executeCancelAction.bind(this)
      );
      return;
    }
    yield this.executeCancelAction();
  }

  executeCancelAction(): void {
    this.cancelled = true;
    this._ownPendingChanges = false;

    this.close({ cancelled: true });
  }

  getState(): InitializeParams {
    return {
      id: this.entityValues.id,
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getPhonogramValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = PhonogramValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
    this.saveOriginalState();
  }

  *_save(): any {
    // Subentidad sin edición standalone: se persiste por el update anidado del parent.
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const idToLoad = params?.id || params?.duplicateFromId;

      if (idToLoad) {
        yield this.loadPhonogramById(idToLoad);

        if (params?.duplicateFromId) {
          this.entityValues.id = "";
        }
      }

      if (params?.callbackData?.filter) {
        Object.assign(this.entityValues, params.callbackData.filter);
      }

      if (params?.uiState?.cardState) {
        Object.assign(this.cardState, params.uiState.cardState);
      }

      if (params?.preloadedData) {
        Object.assign(this.entityValues, params.preloadedData);
      }

      yield this.loadPerformersValues();
      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
